import {
  Box3,
  Color,
  Group,
  LOD,
  Material,
  Mesh,
  MeshStandardMaterial,
  Object3D,
  Texture,
  Vector3,
} from "three";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";

import {
  VILNIUS_AUTHORED_ASSET_BACKGROUND_BATCH_DELAY_MS,
  VILNIUS_AUTHORED_ASSET_BACKGROUND_LOAD_CONCURRENCY,
  VILNIUS_AUTHORED_ASSET_FULL_DETAIL_FOCUS_MOVEMENT_M,
  VILNIUS_AUTHORED_ASSET_FULL_DETAIL_PREFETCH_MARGIN_M,
  VILNIUS_AUTHORED_ASSET_FULL_DETAIL_STABLE_FOCUS_MS,
  VILNIUS_LANDMARK_ASSET_LOAD_CONCURRENCY,
  VILNIUS_LANDMARK_LOD_HYSTERESIS,
  VILNIUS_AUTHORED_ASSET_STREAMING_PREFETCH_MARGIN_M,
  VILNIUS_AUTHORED_ASSET_STREAMING_RADIUS_M,
} from "../../config/landmarks.ts";
import { scheduleAuthoredAssetBackgroundTask } from "./authoredAssetScheduler.ts";
import {
  VILNIUS_AIRPORT_AIRFIELD_ID,
  VILNIUS_AIRPORT_SURFACE_MATERIAL_NAMES,
  VILNIUS_AIRPORT_SURFACE_POLYGON_OFFSET_FACTOR,
  VILNIUS_AIRPORT_SURFACE_POLYGON_OFFSET_UNITS_BY_MATERIAL,
} from "../../config/airport.ts";
import {
  VILNIUS_CIVIC_SQUARE_IDS,
  VILNIUS_CIVIC_SQUARE_SURFACE_MATERIAL_NAMES,
  VILNIUS_CIVIC_SQUARE_SURFACE_POLYGON_OFFSET_FACTOR,
  VILNIUS_CIVIC_SQUARE_SURFACE_POLYGON_OFFSET_UNITS_BY_MATERIAL,
} from "../../config/civicSquares.ts";
import {
  authoredMaterialSeasonTreatment,
} from "../../config/seasons.ts";
import type { EnvironmentSeason } from "../environment.ts";
import type { RenderVector3 } from "../types.ts";
import type { RuntimeTileCameraObstruction } from "./runtimeTileView.ts";

export interface VilniusLandmarkManifestEntry {
  readonly id: string;
  readonly label: string;
  readonly tileId: string;
  readonly position: RenderVector3;
  readonly rotationYRadians: number;
  readonly lodDistanceM: number;
  readonly assetUrl: string;
  readonly lod1Url: string;
  readonly collisionAssetUrl: string;
  /** Empty only when the landmark has no GRPK building footprint (for example a bridge). */
  readonly officialFootprintIds: readonly string[];
  readonly sourceManifest: string;
  /** Large nearby assets may opt into the bounded background loading queue. */
  readonly deferInitialLoad: boolean;
}

export interface VilniusLandmarkDiagnostics {
  readonly landmarkCount: number;
  readonly meshCount: number;
  readonly triangleCount: number;
  readonly landmarkIds: readonly string[];
  readonly loadedLandmarkCount: number;
  readonly loadedLandmarkIds: readonly string[];
  readonly simplifiedLandmarkIds: readonly string[];
}

export interface VilniusLandmarkLoadOptions {
  readonly initialFocus?: Readonly<{ x: number; z: number }>;
  readonly initialRadiusM?: number;
  readonly deferDistant?: boolean;
  /** Render-layer preparation that must finish before an authored asset is revealed. */
  readonly prepareObjectForDisplay?: (object: Object3D) => Promise<void>;
}

export interface VilniusLandmarkEntryPartition {
  readonly initial: readonly VilniusLandmarkManifestEntry[];
  readonly deferred: readonly VilniusLandmarkManifestEntry[];
}

export interface VilniusLandmarkCollection {
  readonly object: Group;
  readonly diagnostics: VilniusLandmarkDiagnostics;
  readonly cameraObstructions: readonly RuntimeTileCameraObstruction[];
  setSeason(season: EnvironmentSeason): void;
  setLandmarkYawOffset(id: string, offsetRadians: number): boolean;
  setLandmarkTransform(
    id: string,
    position: Readonly<RenderVector3>,
    yawRadians: number,
    pitchRadians?: number,
    rollRadians?: number,
  ): boolean;
  getLandmarkObject(id: string): Object3D | null;
  updateVisibility(
    focus: Readonly<{ x: number; z: number }>,
    visibleDistanceMeters: number,
  ): void;
  startDeferredStreaming(): void;
  loadDeferred(): Promise<void>;
  dispose(): void;
}

interface VilniusLandmarkManifest {
  readonly schemaVersion: 1 | 2;
  readonly landmarks: readonly VilniusLandmarkManifestEntry[];
}

const AUTHORED_FLOOR_TEXTURE_ANISOTROPY = 4;
const AUTHORED_FLOOR_MATERIAL_NAMES = new Set([
  "historic_marble",
  "interior_carpet",
  "interior_floor",
  "food_hall_floor",
]);

/** Keep shallow-angle authored floor maps stable while the player moves. */
export function stabilizeAuthoredFloorTextures(
  material: MeshStandardMaterial,
): void {
  if (!AUTHORED_FLOOR_MATERIAL_NAMES.has(material.name)) {
    return;
  }
  const textures = new Set([
    material.map,
    material.roughnessMap,
    material.metalnessMap,
    material.normalMap,
  ]);
  for (const texture of textures) {
    if (
      texture === null ||
      texture.anisotropy >= AUTHORED_FLOOR_TEXTURE_ANISOTROPY
    ) {
      continue;
    }
    texture.anisotropy = AUTHORED_FLOOR_TEXTURE_ANISOTROPY;
    texture.needsUpdate = true;
  }
}

function requireRecord(
  value: unknown,
  label: string,
): Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new Error(`${label} must be an object`);
  }
  return value as Record<string, unknown>;
}

function requireString(value: unknown, label: string): string {
  if (typeof value !== "string" || value.length === 0) {
    throw new Error(`${label} must be a non-empty string`);
  }
  return value;
}

function requireFiniteNumber(value: unknown, label: string): number {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    throw new Error(`${label} must be a finite number`);
  }
  return value;
}

export function parseVilniusLandmarkManifest(
  value: unknown,
): VilniusLandmarkManifest {
  const manifest = requireRecord(value, "landmark manifest");
  if (manifest.schemaVersion !== 1 && manifest.schemaVersion !== 2) {
    throw new Error("landmark manifest schemaVersion must be 1 or 2");
  }
  if (!Array.isArray(manifest.landmarks) || manifest.landmarks.length === 0) {
    throw new Error("landmark manifest must contain landmarks");
  }
  const ids = new Set<string>();
  const landmarks = manifest.landmarks.map((raw, index) => {
    const entry = requireRecord(raw, `landmarks[${index}]`);
    const id = requireString(entry.id, `landmarks[${index}].id`);
    if (ids.has(id)) {
      throw new Error(`landmark manifest contains duplicate id ${id}`);
    }
    ids.add(id);
    const position = requireRecord(
      entry.position,
      `landmarks[${index}].position`,
    );
    if (!Array.isArray(entry.officialFootprintIds)) {
      throw new Error(
        `landmarks[${index}].officialFootprintIds must be an array`,
      );
    }
    return {
      id,
      label: requireString(entry.label, `landmarks[${index}].label`),
      tileId: requireString(entry.tileId, `landmarks[${index}].tileId`),
      position: {
        x: requireFiniteNumber(position.x, `landmarks[${index}].position.x`),
        y: requireFiniteNumber(position.y, `landmarks[${index}].position.y`),
        z: requireFiniteNumber(position.z, `landmarks[${index}].position.z`),
      },
      rotationYRadians: requireFiniteNumber(
        entry.rotationYRadians,
        `landmarks[${index}].rotationYRadians`,
      ),
      lodDistanceM: requireFiniteNumber(
        entry.lodDistanceM,
        `landmarks[${index}].lodDistanceM`,
      ),
      assetUrl: requireString(
        entry.assetUrl,
        `landmarks[${index}].assetUrl`,
      ),
      lod1Url: requireString(
        entry.lod1Url,
        `landmarks[${index}].lod1Url`,
      ),
      collisionAssetUrl: requireString(
        entry.collisionAssetUrl,
        `landmarks[${index}].collisionAssetUrl`,
      ),
      officialFootprintIds: entry.officialFootprintIds.map(
        (footprintId, footprintIndex) =>
          requireString(
            footprintId,
            `landmarks[${index}].officialFootprintIds[${footprintIndex}]`,
          ),
      ),
      sourceManifest: requireString(
        entry.sourceManifest,
        `landmarks[${index}].sourceManifest`,
      ),
      deferInitialLoad: entry.deferInitialLoad === true,
    } satisfies VilniusLandmarkManifestEntry;
  });
  return { schemaVersion: manifest.schemaVersion, landmarks } as VilniusLandmarkManifest;
}

export function partitionVilniusLandmarkEntries(
  entries: readonly VilniusLandmarkManifestEntry[],
  focus: Readonly<{ x: number; z: number }>,
  radiusM: number,
): VilniusLandmarkEntryPartition {
  if (!Number.isFinite(radiusM) || radiusM < 0) {
    throw new Error("landmark initial radius must be finite and non-negative");
  }
  const initial: VilniusLandmarkManifestEntry[] = [];
  const deferred: VilniusLandmarkManifestEntry[] = [];
  const radiusSquared = radiusM * radiusM;
  for (const entry of entries) {
    const deltaX = entry.position.x - focus.x;
    const deltaZ = entry.position.z - focus.z;
    (!entry.deferInitialLoad &&
        deltaX * deltaX + deltaZ * deltaZ <= radiusSquared
      ? initial
      : deferred).push(entry);
  }
  return { initial, deferred };
}

function configureLevel(
  object: Object3D,
  castShadow: boolean,
  landmarkId: string,
): {
  meshCount: number;
  triangleCount: number;
} {
  let meshCount = 0;
  let triangleCount = 0;
  const isCivicSquare = VILNIUS_CIVIC_SQUARE_IDS.has(landmarkId);
  object.traverse((child) => {
    if (!(child instanceof Mesh)) {
      return;
    }
    meshCount += 1;
    child.castShadow =
      !isCivicSquare && castShadow && child.userData.castShadow !== false;
    child.receiveShadow = true;
    const materials = Array.isArray(child.material)
      ? child.material
      : [child.material];
    for (const material of materials) {
      if (!(material instanceof MeshStandardMaterial)) {
        continue;
      }
      stabilizeAuthoredFloorTextures(material);
      if (
        landmarkId === VILNIUS_AIRPORT_AIRFIELD_ID ||
        isCivicSquare
      ) {
        const usesAirportDepthBias =
          landmarkId === VILNIUS_AIRPORT_AIRFIELD_ID &&
          VILNIUS_AIRPORT_SURFACE_MATERIAL_NAMES.has(material.name);
        const usesCivicSquareDepthBias =
          isCivicSquare &&
          VILNIUS_CIVIC_SQUARE_SURFACE_MATERIAL_NAMES.has(material.name);
        if (!usesAirportDepthBias && !usesCivicSquareDepthBias) {
          continue;
        }
        material.polygonOffset = true;
        material.polygonOffsetFactor = usesAirportDepthBias
          ? VILNIUS_AIRPORT_SURFACE_POLYGON_OFFSET_FACTOR
          : VILNIUS_CIVIC_SQUARE_SURFACE_POLYGON_OFFSET_FACTOR;
        material.polygonOffsetUnits = usesAirportDepthBias
          ? VILNIUS_AIRPORT_SURFACE_POLYGON_OFFSET_UNITS_BY_MATERIAL[
            material.name
          ] ?? -1
          : VILNIUS_CIVIC_SQUARE_SURFACE_POLYGON_OFFSET_UNITS_BY_MATERIAL[
            material.name
          ] ?? -1;
        material.needsUpdate = true;
      }
    }
    const index = child.geometry.getIndex();
    triangleCount += index === null
      ? child.geometry.getAttribute("position").count / 3
      : index.count / 3;
  });
  return { meshCount, triangleCount };
}

function disposeObject(object: Object3D): void {
  const materials = new Set<Material>();
  const textures = new Set<Texture>();
  object.traverse((child) => {
    if (!(child instanceof Mesh)) {
      return;
    }
    child.geometry.dispose();
    const meshMaterials = Array.isArray(child.material)
      ? child.material
      : [child.material];
    for (const item of meshMaterials) {
      materials.add(item);
      for (const value of Object.values(item)) {
        if (value instanceof Texture) {
          textures.add(value);
        }
      }
    }
  });
  for (const texture of textures) {
    texture.dispose();
  }
  for (const item of materials) {
    item.dispose();
  }
}

async function loadSceneWithRetry(
  loader: GLTFLoader,
  assetUrl: string,
): Promise<Object3D> {
  try {
    return (await loader.loadAsync(assetUrl)).scene;
  } catch {
    return (await loader.loadAsync(assetUrl)).scene;
  }
}

export async function loadVilniusLandmarks(
  manifestUrl: string,
  fetcher: typeof fetch = fetch,
  options: Readonly<VilniusLandmarkLoadOptions> = {},
): Promise<VilniusLandmarkCollection> {
  const response = await fetcher(manifestUrl);
  if (!response.ok) {
    throw new Error(
      `Could not load Vilnius landmark manifest (${response.status})`,
    );
  }
  const manifest = parseVilniusLandmarkManifest(await response.json());
  const loader = new GLTFLoader();
  const object = new Group();
  object.name = "vilnius-authored-landmarks";
  const cameraObstructions: RuntimeTileCameraObstruction[] = [];
  const landmarksById = new Map<
    string,
    {
      object: LOD;
      baseYawRadians: number;
      visibilityRadiusMeters: number;
    }
  >();
  const simplifiedOnlyEntryIds = new Set<string>();
  let meshCount = 0;
  let triangleCount = 0;
  const materialBaseColors = new Map<MeshStandardMaterial, number>();
  let activeSeason: EnvironmentSeason = "summer";
  let disposed = false;

  function applySeason(
    material: MeshStandardMaterial,
    baseColor: number,
  ): void {
    const treatment = authoredMaterialSeasonTreatment(
      material.name,
      activeSeason,
    );
    material.color
      .setHex(baseColor)
      .lerp(new Color(treatment.tint), treatment.strength);
  }

  function registerSeasonalMaterials(level: Object3D): void {
    level.traverse((child) => {
      if (!(child instanceof Mesh)) {
        return;
      }
      const materials = Array.isArray(child.material)
        ? child.material
        : [child.material];
      for (const material of materials) {
        if (!(material instanceof MeshStandardMaterial)) {
          continue;
        }
        const baseColor = materialBaseColors.get(material) ??
          material.color.getHex();
        materialBaseColors.set(material, baseColor);
        applySeason(material, baseColor);
      }
    });
  }

  async function loadEntry(entry: VilniusLandmarkManifestEntry): Promise<{
    readonly entry: VilniusLandmarkManifestEntry;
    readonly full: Object3D;
    readonly simplified: Object3D;
  }> {
    const levels = await Promise.allSettled([
      loadSceneWithRetry(loader, entry.assetUrl),
      loadSceneWithRetry(loader, entry.lod1Url),
    ]);
    const failure = levels.find(
      (level): level is PromiseRejectedResult => level.status === "rejected",
    );
    if (failure !== undefined) {
      for (const level of levels) {
        if (level.status === "fulfilled") {
          disposeObject(level.value);
        }
      }
      throw failure.reason;
    }
    const [full, simplified] = levels;
    if (
      full.status !== "fulfilled" ||
      simplified.status !== "fulfilled"
    ) {
      throw new Error(`Could not load both visual levels for ${entry.id}`);
    }
    return { entry, full: full.value, simplified: simplified.value };
  }

  async function loadSimplifiedEntry(
    entry: VilniusLandmarkManifestEntry,
  ): Promise<{
    readonly entry: VilniusLandmarkManifestEntry;
    readonly simplified: Object3D;
  }> {
    return {
      entry,
      simplified: await loadSceneWithRetry(loader, entry.lod1Url),
    };
  }

  function landmarkVisibilityRadius(level: Object3D): number {
    const boundsSize = new Box3().setFromObject(level).getSize(new Vector3());
    return Math.hypot(boundsSize.x, boundsSize.z) * 0.5;
  }

  function applyLandmarkMetadata(
    lod: LOD,
    entry: VilniusLandmarkManifestEntry,
  ): void {
    lod.name = `landmark:${entry.id}`;
    lod.position.set(entry.position.x, entry.position.y, entry.position.z);
    lod.rotation.y = entry.rotationYRadians;
    lod.userData = {
      landmarkId: entry.id,
      label: entry.label,
      sourceManifest: entry.sourceManifest,
      officialFootprintIds: [...entry.officialFootprintIds],
    };
  }

  function registerLandmark(
    entry: VilniusLandmarkManifestEntry,
    lod: LOD,
    visibilityRadiusMeters: number,
  ): void {
    object.add(lod);
    landmarksById.set(entry.id, {
      object: lod,
      baseYawRadians: entry.rotationYRadians,
      visibilityRadiusMeters,
    });
    for (const footprintId of entry.officialFootprintIds) {
      cameraObstructions.push({
        id: `building:${entry.tileId}:${footprintId}`,
        object: lod,
      });
    }
  }

  async function attachEntry(
    loaded: Awaited<ReturnType<typeof loadEntry>>,
  ): Promise<void> {
    const { entry, full, simplified } = loaded;
    if (disposed) {
      disposeObject(full);
      disposeObject(simplified);
      return;
    }
    const fullCounts = configureLevel(full, true, entry.id);
    const simplifiedCounts = configureLevel(simplified, false, entry.id);
    meshCount += fullCounts.meshCount + simplifiedCounts.meshCount;
    triangleCount +=
      fullCounts.triangleCount + simplifiedCounts.triangleCount;
    registerSeasonalMaterials(full);
    registerSeasonalMaterials(simplified);

    full.name = `${entry.id}:lod0`;
    simplified.name = `${entry.id}:lod1`;
    const lod = new LOD();
    applyLandmarkMetadata(lod, entry);
    lod.addLevel(full, 0);
    lod.addLevel(
      simplified,
      entry.lodDistanceM,
      VILNIUS_LANDMARK_LOD_HYSTERESIS,
    );
    const fullBounds = new Box3().setFromObject(full);
    const simplifiedBounds = new Box3().setFromObject(simplified);
    fullBounds.union(simplifiedBounds);
    const boundsSize = fullBounds.getSize(new Vector3());
    const visibilityRadiusMeters = Math.hypot(boundsSize.x, boundsSize.z) * 0.5;
    if (options.prepareObjectForDisplay !== undefined) {
      try {
        await options.prepareObjectForDisplay(lod);
      } catch (error) {
        console.warn(
          `Could not prepare authored asset ${entry.id} before display.`,
          error,
        );
      }
    }
    if (disposed) {
      disposeObject(lod);
      return;
    }
    registerLandmark(entry, lod, visibilityRadiusMeters);
  }

  async function attachSimplifiedEntry(
    loaded: Awaited<ReturnType<typeof loadSimplifiedEntry>>,
  ): Promise<void> {
    const { entry, simplified } = loaded;
    if (disposed) {
      disposeObject(simplified);
      return;
    }
    const simplifiedCounts = configureLevel(simplified, false, entry.id);
    meshCount += simplifiedCounts.meshCount;
    triangleCount += simplifiedCounts.triangleCount;
    registerSeasonalMaterials(simplified);
    simplified.name = `${entry.id}:lod1`;
    const lod = new LOD();
    applyLandmarkMetadata(lod, entry);
    lod.addLevel(
      simplified,
      entry.lodDistanceM,
      VILNIUS_LANDMARK_LOD_HYSTERESIS,
    );
    if (disposed) {
      disposeObject(lod);
      return;
    }
    registerLandmark(entry, lod, landmarkVisibilityRadius(simplified));
    simplifiedOnlyEntryIds.add(entry.id);
  }

  async function upgradeSimplifiedEntry(
    entry: VilniusLandmarkManifestEntry,
  ): Promise<void> {
    const landmark = landmarksById.get(entry.id);
    if (landmark === undefined || !simplifiedOnlyEntryIds.has(entry.id)) return;
    const full = await loadSceneWithRetry(loader, entry.assetUrl);
    if (disposed) {
      disposeObject(full);
      return;
    }
    const fullCounts = configureLevel(full, true, entry.id);
    meshCount += fullCounts.meshCount;
    triangleCount += fullCounts.triangleCount;
    registerSeasonalMaterials(full);
    full.name = `${entry.id}:lod0`;
    if (options.prepareObjectForDisplay !== undefined) {
      try {
        await options.prepareObjectForDisplay(full);
      } catch (error) {
        console.warn(
          `Could not prepare authored asset ${entry.id} before display.`,
          error,
        );
      }
    }
    if (disposed) {
      disposeObject(full);
      return;
    }
    landmark.object.addLevel(full, 0);
    landmark.visibilityRadiusMeters = Math.max(
      landmark.visibilityRadiusMeters,
      landmarkVisibilityRadius(full),
    );
    simplifiedOnlyEntryIds.delete(entry.id);
  }

  async function loadEntries(
    entries: readonly VilniusLandmarkManifestEntry[],
    tolerateFailures: boolean,
  ): Promise<void> {
    const concurrency = tolerateFailures
      ? VILNIUS_AUTHORED_ASSET_BACKGROUND_LOAD_CONCURRENCY
      : VILNIUS_LANDMARK_ASSET_LOAD_CONCURRENCY;
    for (
      let start = 0;
      start < entries.length && !disposed;
      start += concurrency
    ) {
      const batch = entries.slice(
        start,
        start + concurrency,
      );
      const loaded = await Promise.allSettled(batch.map(loadEntry));
      if (disposed) {
        for (const result of loaded) {
          if (result.status === "fulfilled") {
            disposeObject(result.value.full);
            disposeObject(result.value.simplified);
          }
        }
        return;
      }
      const failure = loaded.find(
        (result): result is PromiseRejectedResult =>
          result.status === "rejected",
      );
      if (failure !== undefined && !tolerateFailures) {
        for (const result of loaded) {
          if (result.status === "fulfilled") {
            disposeObject(result.value.full);
            disposeObject(result.value.simplified);
          }
        }
        throw failure.reason;
      }
      for (const [index, result] of loaded.entries()) {
        if (result.status === "fulfilled") {
          await attachEntry(result.value);
        } else {
          console.warn(
            `Deferred authored asset ${batch[index]?.id ?? "unknown"} failed to load.`,
            result.reason,
          );
        }
      }
      if (
        tolerateFailures &&
        start + concurrency < entries.length
      ) {
        await new Promise<void>((resolve) => {
          setTimeout(resolve, VILNIUS_AUTHORED_ASSET_BACKGROUND_BATCH_DELAY_MS);
        });
      }
    }
  }

  const partition = options.deferDistant === true &&
      options.initialFocus !== undefined &&
      options.initialRadiusM !== undefined
    ? partitionVilniusLandmarkEntries(
        manifest.landmarks,
        options.initialFocus,
        options.initialRadiusM,
      )
    : { initial: manifest.landmarks, deferred: [] };

  try {
    await loadEntries(partition.initial, false);
  } catch (error) {
    disposeObject(object);
    throw error;
  }

  const diagnostics: VilniusLandmarkDiagnostics = {
    get landmarkCount(): number {
      return manifest.landmarks.length;
    },
    get meshCount(): number {
      return meshCount;
    },
    get triangleCount(): number {
      return triangleCount;
    },
    landmarkIds: manifest.landmarks.map((entry) => entry.id),
    get loadedLandmarkCount(): number {
      return landmarksById.size;
    },
    get loadedLandmarkIds(): readonly string[] {
      return [...landmarksById.keys()];
    },
    get simplifiedLandmarkIds(): readonly string[] {
      return [...simplifiedOnlyEntryIds];
    },
  };
  let deferredLoad: Promise<void> | null = null;
  let deferredStreamingEnabled = false;
  let forceDeferredLoad = false;
  let lastVisibilityFocus: Readonly<{ x: number; z: number }> | null = null;
  let lastVisibleDistanceMeters = 0;
  const pendingDeferredEntries = new Map<
    string,
    VilniusLandmarkManifestEntry
  >();
  const activeDeferredEntryIds = new Set<string>();
  let streamingLoad: Promise<void> | null = null;
  const pendingFullDetailEntries = new Map<
    string,
    VilniusLandmarkManifestEntry
  >();
  const activeFullDetailEntryIds = new Set<string>();
  let fullDetailStreamingLoad: Promise<void> | null = null;
  let fullDetailFocusAnchor: Readonly<{ x: number; z: number }> | null = null;
  let fullDetailFocusStableSinceMs = performance.now();

  function applyLoadedVisibility(): void {
    if (lastVisibilityFocus === null) return;
    for (const landmark of landmarksById.values()) {
      const deltaX = landmark.object.position.x - lastVisibilityFocus.x;
      const deltaZ = landmark.object.position.z - lastVisibilityFocus.z;
      const maximumDistance =
        lastVisibleDistanceMeters + landmark.visibilityRadiusMeters;
      landmark.object.visible =
        deltaX * deltaX + deltaZ * deltaZ <= maximumDistance * maximumDistance;
    }
  }

  function beginStreamingLoad(): void {
    if (streamingLoad !== null || pendingDeferredEntries.size === 0) return;
    streamingLoad = (async () => {
      while (
        !disposed &&
        !forceDeferredLoad &&
        pendingDeferredEntries.size > 0
      ) {
        const next = pendingDeferredEntries.values().next().value as
          | VilniusLandmarkManifestEntry
          | undefined;
        if (next === undefined) break;
        pendingDeferredEntries.delete(next.id);
        if (landmarksById.has(next.id)) continue;
        activeDeferredEntryIds.add(next.id);
        try {
          await scheduleAuthoredAssetBackgroundTask(async () => {
            if (disposed || forceDeferredLoad) return;
            await attachSimplifiedEntry(await loadSimplifiedEntry(next));
          });
          applyLoadedVisibility();
        } catch (error) {
          console.warn(
            `Deferred authored asset ${next.id} failed to load.`,
            error,
          );
        } finally {
          activeDeferredEntryIds.delete(next.id);
        }
        if (pendingDeferredEntries.size > 0) {
          await new Promise<void>((resolve) => {
            setTimeout(
              resolve,
              VILNIUS_AUTHORED_ASSET_BACKGROUND_BATCH_DELAY_MS,
            );
          });
        }
      }
    })().finally(() => {
      streamingLoad = null;
      if (
        !disposed &&
        !forceDeferredLoad &&
        pendingDeferredEntries.size > 0
      ) {
        beginStreamingLoad();
      }
    });
  }

  function beginFullDetailStreamingLoad(): void {
    if (
      fullDetailStreamingLoad !== null ||
      pendingFullDetailEntries.size === 0
    ) {
      return;
    }
    fullDetailStreamingLoad = (async () => {
      while (
        !disposed &&
        !forceDeferredLoad &&
        pendingFullDetailEntries.size > 0
      ) {
        const next = pendingFullDetailEntries.values().next().value as
          | VilniusLandmarkManifestEntry
          | undefined;
        if (next === undefined) break;
        pendingFullDetailEntries.delete(next.id);
        if (!simplifiedOnlyEntryIds.has(next.id)) continue;
        activeFullDetailEntryIds.add(next.id);
        try {
          await scheduleAuthoredAssetBackgroundTask(async () => {
            if (disposed || forceDeferredLoad) return;
            await upgradeSimplifiedEntry(next);
          });
          applyLoadedVisibility();
        } catch (error) {
          console.warn(
            `Deferred authored asset ${next.id} failed to load full detail.`,
            error,
          );
        } finally {
          activeFullDetailEntryIds.delete(next.id);
        }
        if (pendingFullDetailEntries.size > 0) {
          await new Promise<void>((resolve) => {
            setTimeout(
              resolve,
              VILNIUS_AUTHORED_ASSET_BACKGROUND_BATCH_DELAY_MS,
            );
          });
        }
      }
    })().finally(() => {
      fullDetailStreamingLoad = null;
      if (
        !disposed &&
        !forceDeferredLoad &&
        pendingFullDetailEntries.size > 0
      ) {
        beginFullDetailStreamingLoad();
      }
    });
  }

  function queueVisibleDeferredEntries(): void {
    if (
      !deferredStreamingEnabled ||
      forceDeferredLoad ||
      lastVisibilityFocus === null
    ) {
      return;
    }
    const preloadDistance = Math.min(
      VILNIUS_AUTHORED_ASSET_STREAMING_RADIUS_M,
      lastVisibleDistanceMeters +
        VILNIUS_AUTHORED_ASSET_STREAMING_PREFETCH_MARGIN_M,
    );
    const preloadDistanceSquared = preloadDistance * preloadDistance;
    const candidates = partition.deferred
      .filter((entry) => {
        if (
          landmarksById.has(entry.id) ||
          activeDeferredEntryIds.has(entry.id)
        ) {
          return false;
        }
        const deltaX = entry.position.x - lastVisibilityFocus!.x;
        const deltaZ = entry.position.z - lastVisibilityFocus!.z;
        return deltaX * deltaX + deltaZ * deltaZ <= preloadDistanceSquared;
      })
      .sort((left, right) => {
        const leftDistance =
          (left.position.x - lastVisibilityFocus!.x) ** 2 +
          (left.position.z - lastVisibilityFocus!.z) ** 2;
        const rightDistance =
          (right.position.x - lastVisibilityFocus!.x) ** 2 +
          (right.position.z - lastVisibilityFocus!.z) ** 2;
        return leftDistance - rightDistance;
      });
    pendingDeferredEntries.clear();
    for (const entry of candidates) {
      pendingDeferredEntries.set(entry.id, entry);
    }
    beginStreamingLoad();

    if (
      performance.now() - fullDetailFocusStableSinceMs <
        VILNIUS_AUTHORED_ASSET_FULL_DETAIL_STABLE_FOCUS_MS
    ) {
      return;
    }
    const fullDetailCandidates = partition.deferred
      .filter((entry) => {
        if (
          !simplifiedOnlyEntryIds.has(entry.id) ||
          activeFullDetailEntryIds.has(entry.id)
        ) {
          return false;
        }
        const deltaX = entry.position.x - lastVisibilityFocus!.x;
        const deltaZ = entry.position.z - lastVisibilityFocus!.z;
        const fullDetailPreloadDistance = Math.min(
          VILNIUS_AUTHORED_ASSET_STREAMING_RADIUS_M,
          entry.lodDistanceM +
            VILNIUS_AUTHORED_ASSET_FULL_DETAIL_PREFETCH_MARGIN_M,
        );
        return deltaX * deltaX + deltaZ * deltaZ <=
          fullDetailPreloadDistance * fullDetailPreloadDistance;
      })
      .sort((left, right) => {
        const leftDistance =
          (left.position.x - lastVisibilityFocus!.x) ** 2 +
          (left.position.z - lastVisibilityFocus!.z) ** 2;
        const rightDistance =
          (right.position.x - lastVisibilityFocus!.x) ** 2 +
          (right.position.z - lastVisibilityFocus!.z) ** 2;
        return leftDistance - rightDistance;
      });
    pendingFullDetailEntries.clear();
    for (const entry of fullDetailCandidates) {
      pendingFullDetailEntries.set(entry.id, entry);
    }
    beginFullDetailStreamingLoad();
  }

  return {
    object,
    diagnostics,
    cameraObstructions,
    setSeason(season): void {
      activeSeason = season;
      for (const [material, baseColor] of materialBaseColors) {
        applySeason(material, baseColor);
      }
    },
    setLandmarkYawOffset(id, offsetRadians): boolean {
      const landmark = landmarksById.get(id);
      if (landmark === undefined) {
        return false;
      }
      landmark.object.rotation.y = landmark.baseYawRadians + offsetRadians;
      return true;
    },
    setLandmarkTransform(
      id,
      position,
      yawRadians,
      pitchRadians = 0,
      rollRadians = 0,
    ): boolean {
      const landmark = landmarksById.get(id);
      if (landmark === undefined) return false;
      landmark.object.position.set(position.x, position.y, position.z);
      landmark.object.rotation.set(
        pitchRadians,
        yawRadians,
        rollRadians,
        "YXZ",
      );
      return true;
    },
    getLandmarkObject(id): Object3D | null {
      return landmarksById.get(id)?.object ?? null;
    },
    updateVisibility(focus, visibleDistanceMeters): void {
      if (
        fullDetailFocusAnchor === null ||
        (focus.x - fullDetailFocusAnchor.x) ** 2 +
            (focus.z - fullDetailFocusAnchor.z) ** 2 >=
          VILNIUS_AUTHORED_ASSET_FULL_DETAIL_FOCUS_MOVEMENT_M ** 2
      ) {
        fullDetailFocusAnchor = { x: focus.x, z: focus.z };
        fullDetailFocusStableSinceMs = performance.now();
        pendingFullDetailEntries.clear();
      }
      lastVisibilityFocus = { x: focus.x, z: focus.z };
      lastVisibleDistanceMeters = Math.max(0, visibleDistanceMeters);
      applyLoadedVisibility();
      queueVisibleDeferredEntries();
    },
    startDeferredStreaming(): void {
      deferredStreamingEnabled = true;
      queueVisibleDeferredEntries();
    },
    async loadDeferred(): Promise<void> {
      forceDeferredLoad = true;
      pendingDeferredEntries.clear();
      pendingFullDetailEntries.clear();
      deferredLoad ??= (async () => {
        await streamingLoad;
        await fullDetailStreamingLoad;
        for (const entry of partition.deferred) {
          if (!simplifiedOnlyEntryIds.has(entry.id)) continue;
          try {
            await upgradeSimplifiedEntry(entry);
          } catch (error) {
            console.warn(
              `Deferred authored asset ${entry.id} failed to load full detail.`,
              error,
            );
          }
        }
        await loadEntries(
          partition.deferred.filter((entry) => !landmarksById.has(entry.id)),
          true,
        );
        applyLoadedVisibility();
      })();
      await deferredLoad;
    },
    dispose(): void {
      if (disposed) {
        return;
      }
      disposed = true;
      disposeObject(object);
      object.removeFromParent();
      object.clear();
      materialBaseColors.clear();
      pendingDeferredEntries.clear();
      pendingFullDetailEntries.clear();
      activeDeferredEntryIds.clear();
      activeFullDetailEntryIds.clear();
      simplifiedOnlyEntryIds.clear();
      landmarksById.clear();
    },
  };
}
